'use client';

import React, { Dispatch, useEffect } from 'react';
import { useAtomValue, useSetAtom } from 'jotai';
import queryString from 'query-string';
import { canUseWindow } from '@/wag-react/common/xenv/core/canUseWindow';
import { Image } from '../../../wag-react/next-components/image';
import { DataTestIds, ProviderServiceType, QueryStringKeys, SignupFlowSource, UTMCampaign, UTMMedium, UTMSource } from '../../../constants';
import { selectedServiceCategoryAtom, selectedServiceTypeAtom, selectedWalkerInfoAtom, WalkerProfileRates } from '../../../state/bookingFlowAtom';
import { useGetURLQuery, useSetIsOnDemand } from '../../../hooks';
import { createDirectBookingUrl } from '../../../utils/createDirectBookingUrl';
import { BrowseAndBookWalkerProfile, WalkerAvailabilityV6, WalkerProfileService } from '../../../types/api';
import { ServiceDropdownOption } from '../../../containers/CityPageContainer/components/ServiceFilter/components/ServiceDropdown';
import { BookWalkerFab } from '../../../containers/WalkerProfilePageContainer/components/BookWalkerFab';
import { getUtms } from '../../../utils/getUtms';
import { WalkerProfilePageValue } from '../../../containers/WalkerProfilePageContainer/WalkerProfile';
import bookNowPaw from '../../../containers/PCGProfile/components/assets/book-paw.svg';
import { Typography } from '../../../wag-react/components/Typography';
import { Button } from '../../../wag-react/next-components/button';
import { useAuthStore, useBookingStore, useFiltersStore } from '../../../containers/BookingJourney/context';
import { extractDate } from '../helpers/extractDate';
import { checkExpiration } from '../../../containers/BookingJourney/helpers/checkExpiration';
import { filterDisabledDays } from '../../../containers/PCGProfile/components/WalkerSchedule/helpers';
import { AvailableServiceType } from '../../../containers/BookingJourney/components/whippet/data';
import { mapBrowseAndBookWalkerProfileToResponseWalkerProfile } from '@/transformers';

/**
 * Creates dynamic direct booking URL that contains proper query parameters and will be passed
 * into Web App iframe
 * @param {Object} utmsOverride - Contains specific UTMs necessary to attribute an owner
 *                                registration with the right method of signup
 * @returns {string}
 */
const dynamicDirectBookingUrl = (params: {
  walkerProfile: Partial<WalkerProfilePageValue['profile']>;
  query: Record<string, string>;
  utmsOverride: Record<string, string>;
  queryParamsFromServer?: Record<string, string>;
  baseUrl: string;
}) => {
  const {
    query,
    utmsOverride,
    queryParamsFromServer,
    baseUrl,
    walkerProfile: {
      name,
      promoCode,
      pictureUrl
    }
  } = params;
  const defaultUtms = {
    [QueryStringKeys.UTMSource]: UTMSource.Web,
    [QueryStringKeys.UTMMedium]: UTMMedium.WalkerProfile
  };

  // Get existing UTMs from the URL, and use default UTM values if the URL doesn't contain UTMs
  const utms = getUtms({
    ...queryParamsFromServer,
    ...query
  }, defaultUtms);

  // Forward other query params other than utms (eg: gclid, adid)
  const nonUtmQueryParams = {
    ...queryParamsFromServer,
    ...query
  };
  return queryString.stringifyUrl({
    url: baseUrl,
    query: {
      [QueryStringKeys.PromoCode]: promoCode,
      [QueryStringKeys.SignupFlowSource]: SignupFlowSource.WalkerProfile,
      [QueryStringKeys.WalkerName]: name,
      [QueryStringKeys.WalkerImage]: pictureUrl,
      ...nonUtmQueryParams,
      ...utms,
      ...utmsOverride
    }
  });
};
type Props = {
  name: string;
  services: WalkerProfileService[];
  rates: WalkerProfileRates;
  serviceType?: ServiceDropdownOption;
  uuid: string;
  thumbnail: string;
  setIsDirectBookingModalOpen: Dispatch<React.SetStateAction<boolean>>;
  promoCode: string;
  testId?: number;
  baseUrl: string;
  setDirectBookingUrl: Dispatch<React.SetStateAction<string>>;
  walkerProfile?: boolean;
  isPromoted?: boolean;
  queryParamsFromServer?: Record<string, string>;
  bookFab?: boolean;
  availability?: WalkerAvailabilityV6;
  walkerProfileV6?: boolean;
  profile?: BrowseAndBookWalkerProfile;
};
export const DirectBookingButton = ({
  name,
  services,
  rates,
  serviceType,
  isPromoted,
  promoCode,
  uuid,
  thumbnail,
  setIsDirectBookingModalOpen,
  setDirectBookingUrl,
  testId,
  baseUrl,
  walkerProfile = false,
  walkerProfileV6 = false,
  queryParamsFromServer,
  bookFab = false,
  availability,
  profile
}: Props) => {
  const setSelectedWalkerInfo = useSetAtom(selectedWalkerInfoAtom);
  const setSelectedServiceType = useSetAtom(selectedServiceTypeAtom);
  const selectedServiceType = useAtomValue(selectedServiceTypeAtom);
  const setSelectedCategory = useSetAtom(selectedServiceCategoryAtom);
  const setCaregiver = useBookingStore(state => state.setCaregiver);
  const createdAt = useBookingStore(state => state.createdAt);
  const setFilters = useFiltersStore(state => state.setFilters);
  const setJourney = useBookingStore(state => state.setJourney);
  const filters = useFiltersStore(state => state.filters);
  const sessionToken = useAuthStore(state => state.session.sessionToken);
  const step = useBookingStore(state => state.journey.step);
  const setSchedule = useBookingStore(state => state.setSchedule);
  const setAttribution = useBookingStore(state => state.setAttribution);
  const setAvailability = useBookingStore(state => state.setAvailability);
  const addProfileToRecommendedCaregivers = useBookingStore(state => state.addProfileToRecommendedCaregivers);
  const setRecommendedCaregivers = useBookingStore(state => state.setRecommendedCaregivers);
  const isServiceDetails = () => {
    if (!selectedServiceType) {
      return true;
    }

    // TODO extend for other edge cases(start date/end date are not present, address is not present)
    return false;
  };
  useSetIsOnDemand(false);
  const setBookingJourneyState = () => {
    // Start of booking journey state 
    setCaregiver({
      photo: thumbnail,
      uuid,
      name,
      isPromoted
    });
    if (profile) {
      const transformedProfile = mapBrowseAndBookWalkerProfileToResponseWalkerProfile(profile);
      addProfileToRecommendedCaregivers(transformedProfile);
    } else {
      // reset recommended caregivers
      setRecommendedCaregivers([]);
    }
    const {
      isExpired
    } = checkExpiration(createdAt);
    if (availability) {
      const disabledDays = filterDisabledDays({
        availability: availability.availability,
        startingDate: new Date(availability.availability[0].date)
      });
      setAvailability({
        disabledDays
      });
    }
    if (!sessionToken) {
      setJourney({
        step: 'CreateAccount',
        nextStep: 'CaregiverPreferences',
        previousStep: null
      });
    }
    if (isServiceDetails()) {
      setJourney({
        step: 'ServiceDetails',
        nextStep: 'CreateAccount',
        previousStep: null
      });
    }
    if (step === 'BookingConfirmation' && !isExpired) {
      setJourney({
        step: 'RequestService'
      });
    }
    const currentDate = new Date();
    if (!filters?.startDate) {
      setSchedule({
        selectedDays: [extractDate(currentDate)]
      });
      setFilters({
        startDate: extractDate(currentDate)
      });
    }
    if (!filters?.endDate && (serviceType?.value === 'walk' || serviceType?.value === 'drop-in')) {
      setFilters({
        endDate: extractDate(currentDate)
      });
    }
    if (!filters?.endDate && (serviceType?.value === 'boarding' || serviceType?.value === 'sitting')) {
      currentDate.setDate(currentDate.getDate() + 1);
      const tomorrow = currentDate;
      setFilters({
        endDate: extractDate(tomorrow)
      });
    }
    setFilters({
      service: serviceType?.value || selectedServiceType as AvailableServiceType['value']
    });
    if (canUseWindow) {
      const searchParams = new URLSearchParams(window.location.search);
      setAttribution({
        adCreative: searchParams.get('campaignid') || '',
        adGroup: searchParams.get('adgroupid') || '',
        adid: searchParams.get('adid') || '',
        gclid: searchParams.get('gclid') || '',
        utmCampaign: walkerProfile ? UTMCampaign.ProfileLink : UTMCampaign.BookButton,
        ...(serviceType?.value && {
          [QueryStringKeys.UTMMedium]: serviceType.type === ProviderServiceType.Walking ? UTMMedium.CityWalking : UTMMedium.CityBoarding
        }),
        signupFlowSource: SignupFlowSource.CityProfile
      });
    }
  };
  const query = useGetURLQuery();
  const servicesLabels = services.map(service => service.label);
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const location = window.location.pathname;
      if (location.includes('boarding')) {
        setSelectedServiceType('boarding');
        setSelectedCategory('Overnight');
      }
    }
  }, [setSelectedCategory, setSelectedServiceType]);
  const buildDirectBookingUrl = () => {
    setSelectedWalkerInfo({
      pictureUrl: thumbnail,
      uuid,
      promoCode,
      servicesLabels,
      rates,
      name
    });
    let url = createDirectBookingUrl({
      baseUrl,
      query,
      walkerProfile: {
        promoCode,
        name,
        pictureUrl: thumbnail,
        servicesLabels,
        uuid
      },
      utmsOverride: {
        [QueryStringKeys.UTMCampaign]: walkerProfile ? UTMCampaign.ProfileLink : UTMCampaign.BookButton,
        ...(serviceType?.value && {
          [QueryStringKeys.UTMMedium]: serviceType.type === ProviderServiceType.Walking ? UTMMedium.CityWalking : UTMMedium.CityBoarding
        }),
        [QueryStringKeys.SignupFlowSource]: walkerProfile ? SignupFlowSource.WalkerProfile : SignupFlowSource.CityPage
      },
      queryParamsFromServer
    });
    if (baseUrl.indexOf('on-demand') > -1) {
      url = baseUrl;
      setBookingJourneyState();
    }
    setDirectBookingUrl(url);
  };
  if (walkerProfileV6) {
    return <>
        <Button display={['flex', 'none']} justifyContent="center" bg="#32AA80" position="fixed" left="calc(50% - 45vw)" bottom="5%" width="90vw" height="50px" borderRadius="50px" mx="auto" mt={2} opacity={!rates ? 0.5 : 1} disabled={!rates} onMouseEnter={buildDirectBookingUrl} onTouchStart={buildDirectBookingUrl} className="hover:!bg-[#30a079] hover:!opacity-100" id="book-now-button" zIndex={10} onClick={event => {
        event.stopPropagation();
        setIsDirectBookingModalOpen(prev => !prev);
      }} style={{
        transform: 'translate3d(0, 168px, 0)'
      }}>
          <Image src={bookNowPaw} width={24} height={24} position="absolute" left="14px" top="12px" alt="Book Now Paw Icon" />
          <Typography fontFamily="muliBold" color="white" fontSize="14px" m={0}>
            Book
            {' '}
            {name}
            {' '}
            now
          </Typography>
        </Button>
        <Button display="flex" justifyContent="center" bg="#32AA80" position="relative" data-testid="book-pcg-now-button" width="274px" height="42px" borderRadius="50px" mx="auto" mt={2} opacity={!rates ? 0.5 : 1} disabled={!rates} onMouseEnter={buildDirectBookingUrl} onTouchStart={buildDirectBookingUrl} className="hover:!bg-[#30a079] hover:!opacity-100" onClick={event => {
        event.stopPropagation();
        setIsDirectBookingModalOpen(prev => !prev);
      }}>
          <Image src={bookNowPaw} width={20} height={20} position="absolute" left="14px" top="10px" alt="Book Now Paw Icon" />
          <Typography fontFamily="muliBold" color="white" fontSize="14px" m={0}>
            Book
            {' '}
            {name}
            {' '}
            now
          </Typography>
        </Button>
      </>;
  }
  if (walkerProfile) {
    return bookFab ? <BookWalkerFab onClick={() => {
      /**
       * If for some reason the URL query parameters changed,
       * let's make sure we get it so we can pass the right attribution
       */

      setSelectedWalkerInfo({
        rates,
        name,
        pictureUrl: thumbnail,
        uuid,
        promoCode,
        servicesLabels
      });
      const url = dynamicDirectBookingUrl({
        baseUrl,
        utmsOverride: {
          [QueryStringKeys.UTMCampaign]: UTMCampaign.MobileBouncer
        },
        query,
        walkerProfile: {
          promoCode,
          name,
          pictureUrl: thumbnail
        },
        queryParamsFromServer
      });
      setDirectBookingUrl(url);
      setIsDirectBookingModalOpen(prev => !prev);
    }}>
        {`Book ${name}`}
      </BookWalkerFab> : <Button variant="contained" width={1} mt="24px"
    // onMouseEnter is triggered on desktop
    onMouseEnter={buildDirectBookingUrl}
    // onTouchStart is triggered on mobile/tablet
    onTouchStart={buildDirectBookingUrl} onClick={event => {
      event.stopPropagation();
      setIsDirectBookingModalOpen(prev => !prev);
    }}>
        {`Book ${name}`}
      </Button>;
  }
  return <Button target="_blank" variant="contained" mt={[null, '12px']} width={['100%', null, '200px']} height="40px" fontSize={['12px']} minWidth={['unset', '136px']} px="16px" fontFamily="muliBold"
  // onMouseEnter is triggered on desktop
  onMouseEnter={buildDirectBookingUrl}
  // onTouchStart is triggered on mobile/tablet
  onTouchStart={buildDirectBookingUrl} onClick={event => {
    event.stopPropagation();
    setIsDirectBookingModalOpen(prev => !prev);
  }} data-sentry-element="Button" data-sentry-component="DirectBookingButton" data-sentry-source-file="DirectBookingButton.tsx">
      <Typography variant="body2" my="0px" color="white" fontFamily={['muliBold', null, 'muli']} fontSize={['14px', null, '16px']} width="max-content" data-testid={`${DataTestIds.CityServicePage.BrowseAndBookProfile.BookNowButton}-${testId}`} data-sentry-element="Typography" data-sentry-source-file="DirectBookingButton.tsx">
        Book now
      </Typography>
    </Button>;
};